import { useCallback, useMemo } from 'react'

import type { TimeRange } from '@tidbcloud/uikit/biz'
import {
  MRT_SortingState,
  toURLTimeRange,
  urlToTimeRange
} from '@tidbcloud/uikit/biz'
import { useSearchParams } from 'react-router-dom'

import { STATEMENT_V2_DEFAULT_TIME_RANGE } from './statementV2'

export interface StatementV2Pagination {
  curPage: number
  pageSize: number
}

const DEFAULT_PAGE_SIZE = 20

const uniqueStrings = (values: string[]) => [
  ...new Set(values.map((value) => value.trim()).filter(Boolean))
]

function parseListParam(value: string | null): string[] {
  if (!value) {
    return []
  }

  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) {
      return uniqueStrings(
        parsed.filter((item): item is string => typeof item === 'string')
      )
    }
  } catch (_error) {
    // Keep compatibility with legacy comma-separated links.
  }

  return uniqueStrings(value.split(','))
}

function stringifyListParam(values: string[]): string | undefined {
  const normalized = uniqueStrings(values)
  return normalized.length ? JSON.stringify(normalized) : undefined
}

function positiveInteger(value: string | null, fallback: number) {
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback
}

type QueryPatch = Record<string, string | number | undefined>

export function useStatementV2UrlState(
  defaultTimeRange: TimeRange = STATEMENT_V2_DEFAULT_TIME_RANGE
) {
  const [searchParams, setSearchParams] = useSearchParams()

  const updateQuery = useCallback(
    (patch: QueryPatch) => {
      const next = new URLSearchParams(searchParams)
      Object.entries(patch).forEach(([key, value]) => {
        if (value === undefined || value === '') {
          next.delete(key)
        } else {
          next.set(key, String(value))
        }
      })
      setSearchParams(next)
    },
    [searchParams, setSearchParams]
  )

  const from = searchParams.get('from')
  const to = searchParams.get('to')
  const timeRange = useMemo(() => {
    if (from && to) {
      return urlToTimeRange({ from, to })
    }
    return defaultTimeRange
  }, [defaultTimeRange, from, to])

  const setTimeRange = useCallback(
    (value: TimeRange) => {
      updateQuery({ ...toURLTimeRange(value), cur_page: undefined })
    },
    [updateQuery]
  )

  const kinds = useMemo(
    () => parseListParam(searchParams.get('kinds')),
    [searchParams]
  )
  const dbs = useMemo(
    () => parseListParam(searchParams.get('db')),
    [searchParams]
  )
  const sampleUsers = useMemo(
    () => parseListParam(searchParams.get('sample_user')),
    [searchParams]
  )
  const tableNames = useMemo(
    () => parseListParam(searchParams.get('table_names')),
    [searchParams]
  )

  const search = searchParams.get('search') || ''
  const sortRules = useMemo<MRT_SortingState>(
    () =>
      (searchParams.get('sort') || '-sum_latency')
        .split(',')
        .filter(Boolean)
        .map((rule) => ({
          id: rule.startsWith('-') ? rule.slice(1) : rule,
          desc: rule.startsWith('-')
        })),
    [searchParams]
  )
  const pagination = useMemo<StatementV2Pagination>(
    () => ({
      curPage: positiveInteger(searchParams.get('cur_page'), 1),
      pageSize: positiveInteger(
        searchParams.get('page_size'),
        DEFAULT_PAGE_SIZE
      )
    }),
    [searchParams]
  )

  const setSortRules = useCallback(
    (
      updater:
        | MRT_SortingState
        | ((current: MRT_SortingState) => MRT_SortingState)
    ) => {
      const next = typeof updater === 'function' ? updater(sortRules) : updater
      const serialized = next
        .map((rule) => `${rule.desc ? '-' : ''}${rule.id}`)
        .join(',')
      updateQuery({ sort: serialized || undefined, cur_page: undefined })
    },
    [sortRules, updateQuery]
  )

  return {
    timeRange,
    setTimeRange,
    kinds,
    setKinds: (values: string[]) =>
      updateQuery({
        kinds: stringifyListParam(values),
        cur_page: undefined
      }),
    search,
    setSearch: (value: string) =>
      updateQuery({ search: value.trim() || undefined, cur_page: undefined }),
    dbs,
    setDbs: (values: string[]) =>
      updateQuery({ db: stringifyListParam(values), cur_page: undefined }),
    sampleUsers,
    setSampleUsers: (values: string[]) =>
      updateQuery({
        sample_user: stringifyListParam(values),
        cur_page: undefined
      }),
    tableNames,
    setTableNames: (values: string[]) =>
      updateQuery({
        table_names: stringifyListParam(values),
        cur_page: undefined
      }),
    sortRules,
    setSortRules,
    pagination,
    setPagination: (value: StatementV2Pagination) =>
      updateQuery({
        cur_page: value.curPage === 1 ? undefined : value.curPage,
        page_size:
          value.pageSize === DEFAULT_PAGE_SIZE ? undefined : value.pageSize
      })
  }
}
